/**
 * UserSearchInput — Debounced user search with preview card before inviting.
 *
 * Searches users by name or member number via /buddy-group-ops/search-users.
 * When a user is selected from the dropdown, shows a preview card with
 * their name, city/state, and an explicit "Invite" button.
 */

import { useState, useEffect, useRef } from 'react';
import { Search, UserPlus, Loader2, MapPin } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
  searchUsersForGroup,
  type SearchUserResult,
} from '@/services/buddy-group.service';

interface UserSearchInputProps {
  /** Called when user confirms the invite */
  onSelect: (user: SearchUserResult) => void;
  /** Placeholder text */
  placeholder?: string;
  /** Disable the component */
  disabled?: boolean;
  /** User profile IDs to exclude from results (already members, etc.) */
  excludeUserIds?: string[];
  /** Optional rodeo ID for scoping search */
  rodeoId?: string;
  /** Optional group ID to exclude existing group members from results */
  excludeGroupId?: string;
}

export function UserSearchInput({
  onSelect,
  placeholder = 'Search by name or member number...',
  disabled = false,
  excludeUserIds = [],
  rodeoId = '',
  excludeGroupId,
}: UserSearchInputProps) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchUserResult[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedUser, setSelectedUser] = useState<SearchUserResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();

  // Debounced search
  useEffect(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    if (query.length < 2) {
      setResults([]);
      setShowDropdown(false);
      return;
    }

    debounceRef.current = setTimeout(async () => {
      setIsSearching(true);
      setError(null);
      try {
        const res = await searchUsersForGroup(
          query,
          rodeoId,
          excludeGroupId,
          10
        );
        // Filter out excluded user IDs client-side
        const filtered =
          excludeUserIds.length > 0
            ? res.filter(
                (u) => !excludeUserIds.includes(u.user_profile_id)
              )
            : res;
        setResults(filtered);
        setShowDropdown(true);
      } catch (err: any) {
        setError(err?.message ?? 'Search failed');
        setResults([]);
      } finally {
        setIsSearching(false);
      }
    }, 300);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [query, rodeoId, excludeGroupId, excludeUserIds.join(',')]);

  // Close dropdown on click outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handlePickUser = (user: SearchUserResult) => {
    setSelectedUser(user);
    setQuery('');
    setResults([]);
    setShowDropdown(false);
  };

  const handleConfirmInvite = () => {
    if (selectedUser) {
      onSelect(selectedUser);
      setSelectedUser(null);
    }
  };

  const handleClearSelection = () => {
    setSelectedUser(null);
  };

  const location = selectedUser
    ? [selectedUser.city, selectedUser.state].filter(Boolean).join(', ')
    : null;

  return (
    <div ref={containerRef} className="space-y-2">
      {/* Search input — hidden when a user is selected */}
      {!selectedUser && (
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
          <Input
            placeholder={placeholder}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onFocus={() => {
              if (results.length > 0) setShowDropdown(true);
            }}
            className="pl-8 h-9 text-sm"
            disabled={disabled}
          />
          {isSearching && (
            <Loader2 className="absolute right-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 animate-spin text-muted-foreground" />
          )}
        </div>
      )}

      {error && <p className="text-xs text-destructive">{error}</p>}

      {/* Dropdown results */}
      {showDropdown && results.length > 0 && !selectedUser && (
        <div className="rounded-md border bg-popover shadow-md max-h-48 overflow-y-auto">
          {results.map((user) => {
            const loc = [user.city, user.state].filter(Boolean).join(', ');
            return (
              <button
                key={user.user_profile_id}
                type="button"
                className="w-full flex items-center justify-between px-3 py-2 text-sm hover:bg-accent transition-colors text-left"
                onClick={() => handlePickUser(user)}
              >
                <span className="flex items-center gap-2 flex-1 min-w-0">
                  <span className="h-6 w-6 rounded-full bg-muted flex items-center justify-center text-xs font-medium shrink-0">
                    {user.display_name?.charAt(0)?.toUpperCase() ?? '?'}
                  </span>
                  <span className="min-w-0">
                    <span className="truncate block">
                      {user.display_name}
                      {user.member_number && (
                        <span className="text-muted-foreground ml-1.5">
                          #{user.member_number}
                        </span>
                      )}
                    </span>
                    {loc && (
                      <span className="text-[11px] text-muted-foreground truncate block">
                        {loc}
                      </span>
                    )}
                  </span>
                </span>
              </button>
            );
          })}
        </div>
      )}

      {showDropdown &&
        query.length >= 2 &&
        !isSearching &&
        results.length === 0 &&
        !selectedUser && (
          <div className="rounded-md border bg-popover shadow-md p-3">
            <p className="text-xs text-muted-foreground text-center">
              No users found matching "{query}"
            </p>
          </div>
        )}

      {/* Selected user preview — single row */}
      {selectedUser && (
        <div className="flex items-center gap-3 rounded-lg border bg-card px-3 py-2">
          <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center text-sm font-semibold text-primary shrink-0">
            {selectedUser.display_name?.charAt(0)?.toUpperCase() ?? '?'}
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium truncate">
              {selectedUser.display_name}
              {selectedUser.member_number && (
                <span className="text-muted-foreground font-normal ml-1.5">
                  #{selectedUser.member_number}
                </span>
              )}
            </p>
            {location && (
              <p className="text-[11px] text-muted-foreground flex items-center gap-1">
                <MapPin className="h-3 w-3 shrink-0" />
                {location}
              </p>
            )}
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <Button
              size="sm"
              variant="ghost"
              className="h-7 px-2 text-xs"
              onClick={handleClearSelection}
            >
              Cancel
            </Button>
            <Button
              size="sm"
              className="h-7"
              onClick={handleConfirmInvite}
              disabled={disabled}
            >
              <UserPlus className="h-3.5 w-3.5 mr-1.5" />
              Invite
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
